import { useEffect, useRef } from "react";

/**
 * Configuración del widget de Wealth Reader.
 * Documentación: Docs/integracion-via-iframe-1-de-2-frontend.md
 */
export type WealthReaderConfig = {
  /** Id de operación generado por nosotros; vuelve en `statistics.operation_id` del webhook. */
  operation_id: string;
  /** Lista de entity codes a mostrar. Vacío = todas las entidades soportadas. */
  entities_to_display?: string[];
  /** true = enviar info completa al webhook (cuentas + transacciones). Recomendado. */
  wait_full_response?: boolean;
  /** Fecha desde la que pedir transacciones (YYYY-MM-DD). Solo aplica con wait_full_response=true. */
  date_from?: string;
  /** CSV de tipos de producto a recuperar (accounts,portfolios,cards,receipts,loans,…). */
  product_types?: string;
  /** Si se rellena, el widget abre directamente el login de esa entidad. */
  default_login?: string;
  /** País de la entidad cuando se usa default_login. */
  default_login_entity_country?: string;
  /** Token previo para retomar/renovar credenciales de un banco ya conectado. */
  token?: string;
  /** Mostrar entidades PSD2. Default true. Solo aplica si entities_to_display está vacío. */
  psd2?: boolean;
  /** Mostrar entidades no PSD2 (canal alternativo). Default true. */
  nonpsd2?: boolean;
};

type Props = {
  config: WealthReaderConfig;
  /** Se invoca cuando el widget emite "flow completed". */
  onComplete?: (operationId: string) => void;
  /** Alto del iframe en px. Default 640. */
  height?: number;
};

const WIDGET_SCRIPT_SRC = "https://widget.wealthreader.com/js/load.js";
const WIDGET_EXPECTED_ORIGIN = "https://widget.wealthreader.com";

/**
 * Monta el widget de Wealth Reader siguiendo la integración del Docs/:
 *   1) Expone `window.wr_conf` con la configuración.
 *   2) Renderiza un <iframe id="wr-iframe">.
 *   3) Inyecta el script load.js que llena el iframe.
 *   4) Escucha "message" para detectar "flow completed".
 *
 * Nota: el script de WR es global; recargar el componente en la misma sesión
 * vuelve a montar el iframe, pero no recarga load.js (ya cacheado por el browser).
 */
export function WealthReaderWidget({ config, onComplete, height = 640 }: Props) {
  const scriptInjectedRef = useRef(false);

  useEffect(() => {
    // 1) wr_conf debe ser accesible globalmente cuando load.js se ejecute.
    (window as unknown as { wr_conf: WealthReaderConfig }).wr_conf = config;

    // 2) Listener de mensajes.
    const isDev =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const handler = (e: MessageEvent) => {
      // Filtro de seguridad: en producción solo aceptar mensajes del origen del widget.
      // En dev / localhost se relaja para facilitar debugging.
      if (e.origin !== WIDGET_EXPECTED_ORIGIN && !isDev) {
        return;
      }
      if (e.data === "flow completed") {
        console.log("[wr-widget] flujo completado, operation_id:", config.operation_id);
        onComplete?.(config.operation_id);
      }
    };
    window.addEventListener("message", handler);

    // 3) Inyectar load.js una sola vez por sesión.
    if (!scriptInjectedRef.current) {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${WIDGET_SCRIPT_SRC}"]`,
      );
      if (!existing) {
        const script = document.createElement("script");
        script.src = WIDGET_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
      scriptInjectedRef.current = true;
    }

    return () => {
      window.removeEventListener("message", handler);
    };
  }, [config, onComplete]);

  return (
    <iframe
      id="wr-iframe"
      title="Wealth Reader widget"
      width="100%"
      height={height}
      style={{ border: 0 }}
      referrerPolicy="origin"
    />
  );
}
